import React, {Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Card, Table, Button, Icon} from 'antd';
import _ from 'lodash';

import * as mapActions from '../actions/map-actions';
import * as fileUtils from '../utils/file-utils';

import CardMenu from './CardMenu.js';

import './DetailCard.css';

const showSamplePatients = _.includes(window.location.search, 'demo');

const percentileRender = value => {
  const cutoff = 25;
  // const colorPct =  Math.round((1 - value / cutoff) * 20) * .5;
  const colorPct = 1 - value / cutoff;

  return {
    children: value,
    props: {
      style: {
        fontWeight: value <= cutoff ? 800 : 400,
        background: 'rgba(100,172,190,' + colorPct + ')'
      }
    }
  };
};

const expandedRowRender = record => {
  const columns = [
    {title: 'Provider Id', dataIndex: 'npi', key: 'npi'},
    {title: 'Name', dataIndex: 'name', key: 'name'},
    {title: 'Location', dataIndex: 'location', key: 'location'},
    {
      title: 'Specialty',
      dataIndex: 'specialty',
      key: 'specialty',
    }, {
      title: <span>Patient<br/>Count</span>,
      dataIndex: 'beneCount',
      key: 'beneCount',
      width: 120
    }, {
      title: <span>Avg Patient<br/>Risk</span>,
      dataIndex: 'beneAvgRiskScore',
      key: 'beneAvgRiskScore',
      width: 120
    }, {
      title: <span>Opioid<br/>Patient %</span>,
      dataIndex: 'opioidBenePct',
      key: 'opioidBenePct',
      width: 120,
    }, {
      title: <span>Opioid Claims<br/>per Member</span>,
      dataIndex: 'opioidClaimsPm',
      key: 'opioidClaimsPm',
      width: 120
    }, {
      title: <span>Opioid Day<br/>Supply P/M</span>,
      dataIndex: 'opioidDaySupplyPm',
      key: 'opioidDaySupplyPm',
      width: 120
    }
  ];

  const pctColumns = [
    {
      title: 'Patient Id',
      dataIndex: 'id',
      key: 'id'
    }, {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    }, {
      title: 'Location',
      dataIndex: 'location',
      key: 'location'
    }, {
      title: 'Patient Age',
      dataIndex: 'age',
      key: 'age',
      width: 120,
      sorter: (a, b) => a.age - b.age
    }, {
      title: 'Patient Risk',
      dataIndex: 'risk',
      key: 'risk',
      width: 120,
      sorter: (a, b) => a.risk - b.risk
    }, {
      title: 'Opioid Claims',
      dataIndex: 'claimsCount',
      key: 'claimsCount',
      width: 120,
      sorter: (a, b) => a.claimsCount - b.claimsCount
    }, {
      title: 'Opioid Claims %',
      dataIndex: 'claimsPct',
      key: 'claimsPct',
      width: 120,
      sorter: (a, b) => a.claimsPct - b.claimsPct
    }, {
      title: 'Opioid Days Supply',
      dataIndex: 'claimsDays',
      key: 'claimsDays',
      width: 150,
      sorter: (a, b) => a.claimsDays - b.claimsDays
    }
  ];

  const randomPatients = provider => {
    return _.times(Math.round(provider.beneCount * provider.opioidBenePct), i => {
      const claims = Math.round(_.random(0.5, 1.5) * provider.opioidClaimsPm);
      return {
        id: _.random(11111111, 99999999),
        name: 'Patient ' + (i + 1),
        location: provider.location,
        age: Math.round(_.random(0.8, 1.4) * provider.beneAvgAge),
        risk: Math.round(_.random(0.5, 1.5) * provider.beneAvgRiskScore * 10) / 10,
        claimsPct: Math.round(_.random(0.25, 1) * 1000) / 10,
        claimsCount: claims,
        claimsDays: Math.round(claims / provider.opioidClaimsPm * provider.opioidDaySupplyPm),
      }
    })
  };

  return (
    <div style={{padding: '10px'}}>
      <h3>Provider Detail for {record.name}</h3>

      <Table
        style={{marginBottom: 16, marginTop: 0, fontWeight: 600}}
        columns={columns}
        dataSource={[record]}
        size="small"
        bordered={false}
        pagination={false}
      />

      {showSamplePatients &&
      <h3>Provider Opioid Patients Sample</h3>
      }

      {showSamplePatients &&
      <Table
        style={{marginBottom: 10, marginTop: 5}}
        columns={pctColumns}
        dataSource={randomPatients(record)}
        size="small"
        bordered={false}
        pagination={true}
      />
      }
    </div>
  );
};

const fixedColWidth = window.innerWidth > 1600 ? 110 : 82;

class DetailCard extends Component {

  constructor(props) {
    super(props);

    this.state = {
      selectedRowKeys: []
    }
  }

  handleDownload() {
    let filteredData = this.props.providerData.slice();

    if (this.state.selectedRowKeys.length) {
      filteredData = _.intersectionBy(this.props.providerData, this.state.selectedRowKeys, d => {
        return d.hasOwnProperty('npi') ? d.npi : d;
      });
    }

    const filename = _.kebabCase(this.props.selectedCounty.name + ' County Prescribers') + '.csv';
    const csvText = fileUtils.createCsv(filteredData);

    fileUtils.saveTextFile(csvText, filename);
  }

  onSelectChange = (selectedRowKeys) => {
    this.setState({selectedRowKeys});
  };

  render() {
    const rowSelection = {
      selectedRowKeys: this.state.selectedRowKeys,
      onChange: this.onSelectChange,
    };

    const getSpecialtyFilters = data => {
      const specs = _.uniq(_.map(data, 'specialty')).sort();

      return _.map(specs, s => {
        return {
          text: s,
          value: s
        }
      })
    };

    const columns = [{
      title: 'Provider Id',
      dataIndex: 'npi',
      key: 'npi',
      className: 'no-wrap',
      width: 100
    }, {
      title: 'Location',
      dataIndex: 'location',
      className: 'no-wrap',
      key: 'location'
    }, {
      title: 'Specialty',
      dataIndex: 'specialty',
      key: 'specialty',
      className: 'no-wrap',
      filters: getSpecialtyFilters(this.props.providerData),
      onFilter: (value, record) => record.specialty === value,
    }, {
      title: 'Patient Prescribed Opioids Percentile',
      children: [{
        title: <span>US</span>,
        dataIndex: 'natRankOpioidBenePct',
        key: 'natRankOpioidBenePct',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.natRankOpioidBenePct - b.natRankOpioidBenePct
      }, {
        title: <span>State</span>,
        dataIndex: 'stRankOpioidBenePct',
        key: 'stRankOpioidBenePct',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.stRankOpioidBenePct - b.stRankOpioidBenePct
      }, {
        title: <span>County</span>,
        dataIndex: 'cntyRankOpioidBenePct',
        key: 'cntyRankOpioidBenePct',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.cntyRankOpioidBenePct - b.cntyRankOpioidBenePct
      }]
    }, {
      title: 'Opioid Claims per Patient Percentile',
      children: [{
        title: <span>US</span>,
        dataIndex: 'natRankOpioidClaimsPm',
        key: 'natRankOpioidClaimsPm',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.natRankOpioidClaimsPm - b.natRankOpioidClaimsPm
      }, {
        title: <span>State</span>,
        dataIndex: 'stRankOpioidClaimsPm',
        key: 'stRankOpioidClaimsPm',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.stRankOpioidClaimsPm - b.stRankOpioidClaimsPm
      }, {
        title: <span>County</span>,
        dataIndex: 'cntyRankOpioidClaimsPm',
        key: 'cntyRankOpioidClaimsPm',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.cntyRankOpioidClaimsPm - b.cntyRankOpioidClaimsPm
      }]
    }, {
      title: 'Opioid Supply Days per Patient Percentile',
      children: [{
        title: <span>US</span>,
        dataIndex: 'natRankOpioidDaysSupplyPm',
        key: 'natRankOpioidDaysSupplyPm',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.natRankOpioidDaysSupplyPm - b.natRankOpioidDaysSupplyPm
      }, {
        title: <span>State</span>,
        dataIndex: 'stRankOpioidDaysSupplyPm',
        key: 'stRankOpioidDaysSupplyPm',
        width: fixedColWidth,
        render: percentileRender,
        sorter: (a, b) => a.stRankOpioidDaysSupplyPm - b.stRankOpioidDaysSupplyPm
      }, {
        title: <span>County</span>,
        dataIndex: 'cntyRankOpioidDaysSupplyPm',
        key: 'cntyRankOpioidDaysSupplyPm',
        width: fixedColWidth + 10,
        render: percentileRender,
        sorter: (a, b) => a.cntyRankOpioidDaysSupplyPm - b.cntyRankOpioidDaysSupplyPm
      }]
    }
    ];

    const downloadBtn = (
      <Button size="small"
              style={{marginTop: '-4px'}}
              onClick={this.handleDownload.bind(this)}>
        <Icon type="download" style={{color: '#c85a5a'}}/>
        <span className="hidden-sm-down" style={{fontSize: '10px'}}> Download</span>
      </Button>
    );

    const title = (
      <CardMenu showLocations={true} title={
        <span>
          {this.props.selectedCounty.name}
          <span className="hidden-sm-down"> County Medicare Part D</span> Prescribers
        </span>
      }/>
    );

    return (
      <Card className="DetailCard" noHovering={true} title={title} extra={downloadBtn} loading={this.props.loading}>
        <Table
          rowKey="npi"
          size="middle"
          bordered={true}
          pagination={true}
          showHeader={true}
          columns={columns}
          rowSelection={rowSelection}
          expandedRowRender={expandedRowRender}
          dataSource={this.props.providerData}/>

        <div className="note">
          <span>
            * All patient counts only include Part D Medicare which accounts for 2/3rd of the Medicare population
          </span>
        </div>
      </Card>
    );
  }
}

function mapStoreToProps(store, props) {
  return {
    loading: store.providers.loading,
    providerData: store.providers.data,
    selectedCounty: store.counties.selected
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(mapActions, dispatch)
  }
}

export default connect(mapStoreToProps, mapDispatchToProps)(DetailCard);



// WEBPACK FOOTER //
// ./src/containers/DetailCard.js